import { useMemo, useState } from 'react';
import useSWRInfinite from 'swr/infinite';

// Utils
import { MENTIONS } from '@/lib/routes/apiEndpoints';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import { get } from '@/utils/client';

const getKey = (pageIndex, previousPageData, activeSPID, limit, type) => {
    // Missing parameter
    if (!activeSPID) {
        return null;
    }

    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    const apiUrl = injectEndpointUrlParams(MENTIONS, {
        SPID: activeSPID
    });

    const queryParams = `per_page=${limit}${type && `&type=${type}`}`;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return `${apiUrl}?${queryParams}`;
    }

    // Add the cursor to the API endpoint
    return `${apiUrl}?${queryParams}&cursor=${previousPageData.cursor}`;
};

const useMentions = ({ activeSPID, limit = 20, options = {}, shouldFetch = true }) => {
    const [type, setType] = useState('');
    const {
        data: mentionsData,
        error,
        mutate,
        setSize,
        size
    } = useSWRInfinite(
        shouldFetch
            ? (pageIndex, previousPageData) =>
                  getKey(pageIndex, previousPageData, activeSPID, limit, type)
            : null,
        (url) => get(url).then((res) => res.data),
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false,
            ...options
        }
    );

    const mentions = useMemo(() => mentionsData?.flatMap(({ data }) => data) || [], [mentionsData]);
    // Check if it's the initial loading (not the load more)
    const isLoadingInitialData = !mentionsData && !error;
    // Check if the load more is loading
    const isLoadingMore = size > 0 && mentionsData && typeof mentionsData[size - 1] === 'undefined';
    // Check if there is a cursor to active the load more button
    const isMoreToBeLoaded =
        mentionsData && typeof mentionsData[mentionsData.length - 1].cursor === 'string';

    return {
        error,
        isLoadingInitialData,
        isLoadingMore,
        isMoreToBeLoaded,
        mentions,
        mutate,
        loadMore: () => setSize(size + 1),
        setType,
        unreadCount: mentionsData?.[0]?.summary.unread_count || 0
    };
};

export default useMentions;
